/**
 * Upgrade prompts: the pop-ups and banners that offer a paying coach a change
 * to her plan or the Accelerator, and the rules that pick which one she sees.
 *
 * Each prompt is one entry in `UPGRADE_PROMPT_RULES`: pop-up or banner, the
 * offer it makes, the screens it opens on, and the paid days it shows on. The
 * server reads her account, her dismissals and the live offers, and these pure
 * functions pick at most one prompt for the screen. The client only renders
 * the answer.
 *
 * Rules every prompt follows:
 * - Only a paying coach sees one: lifecycle `paid`, not past due, and not on
 *   the program lane, whose coaches are sold to directly.
 * - One prompt per screen. A pop-up comes before a banner.
 * - A dismissed prompt stays dismissed, on every device. Each pop-up shows
 *   once by itself; after that only a link or a button opens it.
 * - After she dismisses any prompt, no prompt shows for a day. This keeps the
 *   brief's "never show two offers in the same session" with a rule the server
 *   can check: it cannot see a browser session, and a day is never shorter
 *   than one.
 * - The yearly pop-up waits a week after the Accelerator pop-up, so she is
 *   never offered both in the same week.
 * - No prompt that sells NOVA Pro in her first 30 paid days.
 *
 * A link (`?offer=` on NOVA, sent in the upgrade emails) or a button (the
 * yearly banner, the Accelerator card on the course page) asks for one offer's
 * pop-up. It shows whatever the paid days, earlier dismissals and quiet
 * periods say, but only to a coach who can see a prompt at all and only while
 * the offer is still hers.
 *
 * Paid days count from her first NOVA payment (`novaPaidStartedAt`). Day 0 is
 * the 24 hours after it.
 *
 * Aliases: upgrade prompts, upgrade pop-up, yearly banner, switch to yearly
 * pop-up, day 15 pop-up, day 0 pop-up, accelerator pop-up, join the
 * accelerator pop-up, upsell prompts, prompt dismissal, offer link, upgrade
 * email link
 */

import {
  formatAcceleratorPrice,
  type AcceleratorCallSchedule,
  type AcceleratorPrice,
} from "~/lib/shared/accelerator-offer";
import { nextCallAt } from "~/lib/shared/call-schedules";
import type { CoachLifecycle } from "~/lib/shared/coach-account";
import {
  formatExactStripeAmount,
  formatStripeAmount,
} from "~/lib/shared/currency";

/** Every prompt, in the order they win a screen within their kind. */
export const UPGRADE_PROMPT_KEYS = [
  "accelerator_popup",
  "yearly_popup",
  "yearly_banner",
] as const;
export type UpgradePromptKey = (typeof UPGRADE_PROMPT_KEYS)[number];

/**
 * The screens a prompt can open on: Welcome, where she lands after logging
 * in, and the four NOVA tabs (NOVA Home, My assets, My content, What NOVA
 * knows). None of them makes a payment its main ask.
 */
export const UPGRADE_PROMPT_SURFACES = ["welcome", "nova_tab"] as const;
export type UpgradePromptSurface = (typeof UPGRADE_PROMPT_SURFACES)[number];

export type UpgradePromptKind = "popup" | "banner";

/** What a prompt sells. The server reads an offer only when a prompt that
 *  makes it could show. */
export const UPGRADE_OFFER_KINDS = ["accelerator", "switch_yearly"] as const;
export type UpgradeOfferKind = (typeof UPGRADE_OFFER_KINDS)[number];

/**
 * The `offer` values a NOVA link takes, as the upgrade emails write them:
 * `/my-coaching/business/nova?offer=accelerator` and `?offer=yearly`.
 */
export const UPGRADE_OFFER_LINKS = ["accelerator", "yearly"] as const;
type UpgradeOfferLink = (typeof UPGRADE_OFFER_LINKS)[number];

/** The offer a link value asks for. */
export const UPGRADE_OFFER_FOR_LINK = {
  accelerator: "accelerator",
  yearly: "switch_yearly",
} as const satisfies Record<UpgradeOfferLink, UpgradeOfferKind>;

export interface UpgradePromptRule {
  kind: UpgradePromptKind;
  offer: UpgradeOfferKind;
  surfaces: readonly UpgradePromptSurface[];
  /**
   * The paid days it shows on: from `from` up to, not including, `until`, or
   * every day from `from` without `until`. Null means every day, including
   * when her first payment date is unknown.
   */
  paidDays: { from: number; until?: number } | null;
  /** It sells NOVA Pro, which no prompt may do in her first 30 paid days. */
  promotesPro: boolean;
  /** It waits this long after she last dismissed `prompt`. */
  waitsAfter?: { prompt: UpgradePromptKey; ms: number };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The Accelerator pop-up on her first visit after her first payment, the
 * yearly pop-up on her first visit from paid day 15, and the yearly banner on
 * the four NOVA tabs. The Accelerator pop-up ends the day before the yearly
 * one starts, so the two never compete for a screen, and the yearly pop-up
 * waits a week after the Accelerator one, which a link can open late.
 */
export const UPGRADE_PROMPT_RULES = {
  accelerator_popup: {
    kind: "popup",
    offer: "accelerator",
    surfaces: ["welcome", "nova_tab"],
    paidDays: { from: 0, until: 15 },
    promotesPro: false,
  },
  yearly_popup: {
    kind: "popup",
    offer: "switch_yearly",
    surfaces: ["welcome", "nova_tab"],
    paidDays: { from: 15 },
    promotesPro: false,
    waitsAfter: { prompt: "accelerator_popup", ms: 7 * DAY_MS },
  },
  yearly_banner: {
    kind: "banner",
    offer: "switch_yearly",
    surfaces: ["nova_tab"],
    paidDays: null,
    promotesPro: false,
  },
} as const satisfies Record<UpgradePromptKey, UpgradePromptRule>;

export interface UpgradePromptEntry<
  K extends string = UpgradePromptKey,
> extends UpgradePromptRule {
  key: K;
}

/** Every prompt with its rule, in `UPGRADE_PROMPT_KEYS` order. */
export const UPGRADE_PROMPTS: readonly UpgradePromptEntry[] =
  UPGRADE_PROMPT_KEYS.map((key) => ({ key, ...UPGRADE_PROMPT_RULES[key] }));

/** The pop-up that makes an offer. Each offer has exactly one. */
export function upgradePopupFor(offer: UpgradeOfferKind): UpgradePromptKey {
  const popup = UPGRADE_PROMPTS.find(
    (entry) => entry.kind === "popup" && entry.offer === offer,
  );
  if (!popup) throw new Error(`No pop-up makes the offer ${offer}`);
  return popup.key;
}

/** How long after any dismissal no prompt shows. */
export const UPGRADE_PROMPT_QUIET_MS = DAY_MS;

/** Paid days in which no prompt sells NOVA Pro. */
export const PRO_PROMOTION_FREE_PAID_DAYS = 30;

export interface UpgradePromptDismissal<K extends string = UpgradePromptKey> {
  prompt: K;
  /** Her latest dismissal of it. */
  dismissedAt: Date;
}

/** Everything the rules read, other than the offers. */
export interface UpgradePromptFacts<K extends string = UpgradePromptKey> {
  surface: UpgradePromptSurface;
  lifecycle: CoachLifecycle;
  pastDue: boolean;
  /** She came in through the program lane (`acquisitionLane === "program"`). */
  programLane: boolean;
  /** Her first NOVA payment (`novaPaidStartedAt`), or null when not recorded. */
  paidStartedAt: Date | null;
  dismissals: readonly UpgradePromptDismissal<K>[];
  now: Date;
}

/** Whole days since her first payment: 0 on the day of it. Null when unknown. */
export function paidDay(paidStartedAt: Date | null, now: Date): number | null {
  if (!paidStartedAt) return null;
  return Math.floor((now.getTime() - paidStartedAt.getTime()) / DAY_MS);
}

function inPaidDays(
  window: UpgradePromptRule["paidDays"],
  day: number | null,
): boolean {
  if (!window) return true;
  return (
    day !== null &&
    day >= window.from &&
    (window.until === undefined || day < window.until)
  );
}

/** A paying coach outside the program lane; nobody else sees a prompt. */
function seesPrompts(facts: UpgradePromptFacts<string>): boolean {
  return facts.lifecycle === "paid" && !facts.pastDue && !facts.programLane;
}

/** An unknown first payment is not known to be past the first 30 days. */
function proAllowed(prompt: UpgradePromptRule, day: number | null): boolean {
  return (
    !prompt.promotesPro || (day !== null && day >= PRO_PROMOTION_FREE_PAID_DAYS)
  );
}

/**
 * The prompts that could show on this screen, pop-ups first, before any offer
 * is read. Empty when she sees no prompt whatever the offers are.
 *
 * `prompts` is the list to choose from, in priority order within each kind.
 */
export function upgradePromptCandidates<K extends string = UpgradePromptKey>(
  facts: UpgradePromptFacts<K>,
  prompts: readonly UpgradePromptEntry<K>[],
): UpgradePromptEntry<K>[] {
  if (!seesPrompts(facts)) return [];
  const now = facts.now.getTime();
  const quiet = facts.dismissals.some(
    (dismissal) =>
      now - dismissal.dismissedAt.getTime() < UPGRADE_PROMPT_QUIET_MS,
  );
  if (quiet) return [];

  const day = paidDay(facts.paidStartedAt, facts.now);
  const dismissedAt = new Map<string, Date>(
    facts.dismissals.map((dismissal) => [
      dismissal.prompt,
      dismissal.dismissedAt,
    ]),
  );
  const waited = (prompt: UpgradePromptRule): boolean => {
    if (!prompt.waitsAfter) return true;
    const after = dismissedAt.get(prompt.waitsAfter.prompt);
    return !after || now - after.getTime() >= prompt.waitsAfter.ms;
  };
  const open = prompts.filter(
    (prompt) =>
      prompt.surfaces.includes(facts.surface) &&
      !dismissedAt.has(prompt.key) &&
      inPaidDays(prompt.paidDays, day) &&
      waited(prompt) &&
      proAllowed(prompt, day),
  );
  return [
    ...open.filter((prompt) => prompt.kind === "popup"),
    ...open.filter((prompt) => prompt.kind === "banner"),
  ];
}

/**
 * The pop-up a link asks for, as the one candidate: `offer`'s pop-up on this
 * screen, whatever its paid days, her dismissals and the quiet periods say.
 * Empty for a coach who sees no prompt at all, so a late email link opens
 * NOVA without one.
 */
export function requestedUpgradePromptCandidates<
  K extends string = UpgradePromptKey,
>(
  facts: UpgradePromptFacts<K>,
  offer: UpgradeOfferKind,
  prompts: readonly UpgradePromptEntry<K>[],
): UpgradePromptEntry<K>[] {
  if (!seesPrompts(facts)) return [];
  const day = paidDay(facts.paidStartedAt, facts.now);
  return prompts.filter(
    (prompt) =>
      prompt.kind === "popup" &&
      prompt.offer === offer &&
      prompt.surfaces.includes(facts.surface) &&
      proAllowed(prompt, day),
  );
}

/**
 * Switching her monthly plan to yearly: what she pays each month now, and the
 * yearly price the switch moves her to, both in the same currency.
 */
export interface SwitchYearlyOffer {
  monthlyAmountCents: number;
  yearlyAmountCents: number;
  currency: string;
}

/**
 * The Accelerator as the pop-up sells it: its Stripe price and, when the
 * server could read it, when the group call runs. Without a schedule the
 * pop-up names no date.
 */
export interface AcceleratorOffer {
  price: AcceleratorPrice;
  call: AcceleratorCallSchedule | null;
}

/** Each offer as the server read it, or null when it has none for her. */
export interface UpgradeOffers {
  accelerator: AcceleratorOffer | null;
  switch_yearly: SwitchYearlyOffer | null;
}

export type UpgradeOffer =
  | ({ kind: "accelerator" } & AcceleratorOffer)
  | ({ kind: "switch_yearly" } & SwitchYearlyOffer);

/** The one prompt a screen shows, with the offer it makes. */
export interface UpgradePrompt<K extends string = UpgradePromptKey> {
  key: K;
  kind: UpgradePromptKind;
  offer: UpgradeOffer;
}

/** What a year costs her less than twelve monthly payments. */
export function yearlySavingCents(offer: SwitchYearlyOffer): number {
  return offer.monthlyAmountCents * 12 - offer.yearlyAmountCents;
}

function liveOffer(
  kind: UpgradeOfferKind,
  offers: UpgradeOffers,
): UpgradeOffer | null {
  switch (kind) {
    case "accelerator": {
      const offer = offers.accelerator;
      // A free price is no sale: the offer page sells nothing at no amount.
      return offer && offer.price.amountCents > 0 ? { kind, ...offer } : null;
    }
    case "switch_yearly": {
      const offer = offers.switch_yearly;
      // A monthly price below a twelfth of the yearly one (a grandfathered
      // price) makes yearly the dearer choice, so there is nothing to offer.
      return offer && yearlySavingCents(offer) > 0 ? { kind, ...offer } : null;
    }
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown upgrade offer: ${String(exhaustive)}`);
    }
  }
}

/** The first candidate whose offer she has, or null. */
export function pickUpgradePrompt<K extends string = UpgradePromptKey>(
  candidates: readonly UpgradePromptEntry<K>[],
  offers: UpgradeOffers,
): UpgradePrompt<K> | null {
  for (const candidate of candidates) {
    const offer = liveOffer(candidate.offer, offers);
    if (offer) return { key: candidate.key, kind: candidate.kind, offer };
  }
  return null;
}

const WHOLE_UNITS = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
} as const;

/**
 * The brief's headline, from her own prices: "$100/mo → $83/mo when you go
 * yearly. Same everything, $203 less." The yearly price per month is rounded
 * to whole units, as the brief shows it.
 */
export function switchYearlyHeadline(offer: SwitchYearlyOffer): string {
  const { currency } = offer;
  const monthly = formatExactStripeAmount(offer.monthlyAmountCents, currency);
  const yearlyPerMonth = formatStripeAmount(
    Math.round(offer.yearlyAmountCents / 12),
    currency,
    WHOLE_UNITS,
  );
  const saving = formatExactStripeAmount(yearlySavingCents(offer), currency);
  return `${monthly}/mo → ${yearlyPerMonth}/mo when you go yearly. Same everything, ${saving} less.`;
}

/**
 * The yearly banner's line: "Switch to yearly and get 2 months of NOVA free."
 * It is stated only while it is true, a year costing her no more than ten
 * monthly payments; otherwise the banner states her own prices.
 */
export function yearlyBannerTitle(offer: SwitchYearlyOffer): string {
  return yearlySavingCents(offer) >= offer.monthlyAmountCents * 2
    ? "Switch to yearly and get 2 months of NOVA free."
    : switchYearlyHeadline(offer);
}

/** What one client is worth in the price line, in cents: "$3,000". */
const ACCELERATOR_CLIENT_FEE_CENTS = 300_000;

/**
 * The price line: "$997 paid once, on top of your NOVA plan. One client at
 * $3,000 covers it three times." The ratio is stated only while it is true: a
 * dearer price, or one in another currency, leaves it out.
 */
export function acceleratorPriceLine(price: AcceleratorPrice): string {
  const line = `${formatAcceleratorPrice(price)} paid once, on top of your NOVA plan.`;
  const coveredThreeTimes =
    price.currency === "usd" &&
    price.amountCents * 3 <= ACCELERATOR_CLIENT_FEE_CENTS;
  return coveredThreeTimes
    ? `${line} One client at $3,000 covers it three times.`
    : line;
}

/**
 * The third bullet: "You can join in any week. The next call is on Tuesday,
 * October 13." The date is the day of the next call on the viewer's clock,
 * which can differ from the call's own. Without a schedule it ends after "any
 * week."
 */
export function acceleratorJoinLine(
  call: AcceleratorCallSchedule | null,
  now: Date,
  viewerTimeZone: string,
): string {
  const line = "You can join in any week.";
  if (!call) return line;
  const date = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: viewerTimeZone,
  }).format(nextCallAt(call, now));
  return `${line} The next call is on ${date}.`;
}
